import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { ICmsConfigSaveService } from '@icms/admin/data/config/icms-config-save.service';
import { ICmsUtilSimpleAbstractComponent } from '@icms/admin/util/simple/icms-admin-util-simple.abstract.component';
import { ICmsCoreAppSchemaService } from '@icms/core/app/icms-core-app-schema.service';
import { NICmsContentField } from '@icms/core/content';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { ICmsConfig } from '@icms/core/resource/config/icms-config';
import { NICmsTray } from '@icms/ui/tray/icms-tray.interfaces';
import { ICmsTrayItemComponent } from '@icms/ui/tray/item/icms-tray-item.component';

export namespace NCmsMetaDataSchema {
    export const configId = 'metaDataCustomSchema';

    export interface IData {
        schema?: string;
    }
}

ICmsContentService.registerConfig({
    id: NCmsMetaDataSchema.configId,
    name: 'Custom meta schema',
    selfManaged: true,
    fields: [
        {
            key: 'schema',
            title: 'Meta data',
            type: NICmsContentField.EFieldType.textarea,
            description: `Custom json to override default schema.org data.<br>
            Default will be merged with this, you can override existing values here or add addtitional information.<br>
            Example:<br>{"@type":"Website","url":"example.com"}.<br>
            Use array for multiple entries:<br>[{"@type":"SomethingNew", name : "Some name"},{"@type":"Website","url":"example.com"}]
            `,
            mirrorEditable: true
        }
    ]
});

@Component({
    standalone: false,
    selector: 'icms-admin-editor-sidebar-settings-meta-data-schema',
    templateUrl: './icms-admin-editor-sidebar-settings-meta-data-schema.html',
    styleUrls: ['./icms-admin-editor-sidebar-settings-meta-data-schema.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ICmsAdminEditorSidebarSettingsMetaDataComponent
    extends ICmsUtilSimpleAbstractComponent
    implements NICmsTray.ITrayBody<void>, OnInit
{
    data!: void;
    protected config!: ICmsConfig;
    protected fields!: NICmsContentField.IField[];
    protected saveStatus = new Status();
    protected loadStatus = new Status();
    protected readonly schemaExample = '{"@type":"Website","url":"example.com"}';

    private iCmsAppSchemaService = inject(ICmsCoreAppSchemaService);
    private iCmsContentService = inject(ICmsContentService);
    private iCmsConfigSaveService = inject(ICmsConfigSaveService);
    private iCmsTrayItemComponent = inject(ICmsTrayItemComponent<void, ICmsAdminEditorSidebarSettingsMetaDataComponent>);

    constructor() {
        super();
    }

    override async ngOnInit() {
        this.fields = this.iCmsContentService.getConfig(NCmsMetaDataSchema.configId)?.fields || [];
        this.subscriptions.add(
            this.iCmsAppSchemaService.config$.subscribe(async config => {
                if (!config) {
                    this.loadStatus.busy = true;
                    this.detectChanges();
                    const config = await this.iCmsAppSchemaService.load();
                    if (!config) {
                        this.iCmsTrayItemComponent.close();
                    }
                    return;
                }
                this.config = config;
                this.loadStatus.busy = false;
                this.detectChanges();
            })
        );
        super.ngOnInit();
    }

    protected async save(): Promise<void> {
        this.saveStatus.busy = true;
        this.detectChanges();
        await this.iCmsConfigSaveService.save(this.config);
        // 1= >   this.structuralDataService.setCustomSchema(this.data.schema);
        this.saveStatus.busy = false;
        this.detectChanges();
    }

    protected isRequired(field: NICmsContentField.IField, data: any): boolean {
        return field.required ? typeof field.required === 'boolean' || !field.required(data) : false;
    }
}
